import React, { memo, useEffect, useRef, useState } from "react";
import mapboxSdk from "@mapbox/mapbox-sdk/services/static";
import get from "lodash/get";
import {
  getContextFactor,
  expandBounds,
  expandBoundsForContext,
  getMapStyleRef,
  detailDimensions,
} from "@/helpers/mapbox";

const StaticMapboxComponent = ({
  center = [-112.074, 33.4484],
  zoom = 10,
  radiusMiles = 10,
  height = 400,
  width = 800,
  polygonCoordinates,
  showPolygon = false,
  padding = 50,
  pendingPolygon = false,
}) => {
  const [mapUrl, setMapUrl] = useState("");
  const wrapperRef = useRef(null);
  // Every Static Images URL is a billed request, so only build it once the card
  // is near the viewport. Sticky on purpose: tearing it down off-screen would
  // refetch on every scroll back. No IntersectionObserver (old browsers, jsdom)
  // means load immediately, exactly as before.
  const [isVisible, setIsVisible] = useState(
    () => typeof window === "undefined" || !window.IntersectionObserver,
  );

  useEffect(() => {
    if (isVisible || !wrapperRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Overlay vertices get baked into the Static Images URL (8,192-char cap), so
  // multiple detailed zip boundaries overflow it and Mapbox returns a blank map.
  const MAX_OVERLAY_POINTS = 300;

  // Sample a ring down to maxPoints, keeping the last vertex so it stays closed.
  const samplePolygon = (points, maxPoints) => {
    if (points.length <= maxPoints) return points;
    const step = Math.ceil(points.length / maxPoints);
    const sampled = points.filter((_, i) => i % step === 0);
    const last = points[points.length - 1];
    if (sampled[sampled.length - 1] !== last) sampled.push(last);
    return sampled;
  };

  // Calculate bounding box from an array of coordinates
  const calculateBounds = (coordinates) => {
    const lngs = coordinates.map((coord) => coord[0]);
    const lats = coordinates.map((coord) => coord[1]);
    return [
      Math.min(...lngs),
      Math.min(...lats),
      Math.max(...lngs),
      Math.max(...lats),
    ];
  };

  // Create a circle polygon as an array of coordinates
  const createCirclePolygon = (centerLng, centerLat, radiusKm) => {
    const steps = 40; // Number of points to create the circle
    const coords = [];

    const kmPerDegreeLat = 111.32; // km per degree latitude
    const kmPerDegreeLng = 111.32 * Math.cos((centerLat * Math.PI) / 180); // km per degree longitude

    for (let i = 0; i < steps; i++) {
      const angle = (i * 360) / steps;
      const angleRadians = (angle * Math.PI) / 180;

      const dx = radiusKm * Math.cos(angleRadians);
      const dy = radiusKm * Math.sin(angleRadians);

      const lng = centerLng + dx / kmPerDegreeLng;
      const lat = centerLat + dy / kmPerDegreeLat;

      coords.push([lng, lat]);
    }

    // Close the polygon by repeating the first coordinate
    if (coords.length > 0) {
      coords.push(coords[0]);
    }

    return coords;
  };

  useEffect(() => {
    // pendingPolygon: the card will show zip boundaries that haven't arrived
    // yet, so don't pay for the circle version it would immediately replace.
    if (!isVisible || pendingPolygon) return;

    // Retrieve the token from environment variables
    const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    // If access token is not found, fallback to displaying only the marker
    if (!accessToken) {
      console.error("Error: Mapbox access token not found");
      setMapUrl("");
      return;
    }

    // Create a client for the Static API
    const staticClient = mapboxSdk({
      accessToken: accessToken,
    });

    // Convert center to an array of numbers
    const numericCenter = Array.isArray(center)
      ? center.map((coord) => Number(coord))
      : [-123.2259181, 44.0431493];

    const { owner: styleOwner, styleId } = getMapStyleRef();
    const { width: reqWidth, height: reqHeight } = detailDimensions(
      width,
      height,
    );

    const generateMap = () => {
      try {
        // Basic request parameters
        const requestOptions = {
          ownerId: styleOwner,
          styleId: styleId,
          width: reqWidth,
          height: reqHeight,
          position: {
            coordinates: numericCenter,
            zoom: zoom,
          },
          highRes: true,
          attribution: false,
        };

        // Define overlays (marker and circle/polygon)
        const overlays = [];

        // If polygon should be shown and coordinates are provided
        if (showPolygon && polygonCoordinates?.length > 0) {
          // Collect all coordinates across all polygons for bounds calculation
          const allCoords = [];
          polygonCoordinates.forEach((polygon) => {
            polygon[0].forEach((p) => {
              allCoords.push([
                Number(p.lng.toFixed(6)),
                Number(p.lat.toFixed(6)),
              ]);
            });
          });

          const bounds = expandBoundsForContext(calculateBounds(allCoords));

          // Adjust view to (expanded) bounds with padding
          requestOptions.position = {
            bbox: bounds,
          };
          requestOptions.padding = String(padding);

          const perPolygonBudget = Math.max(
            1,
            Math.floor(MAX_OVERLAY_POINTS / polygonCoordinates.length),
          );

          polygonCoordinates.forEach((polygon) => {
            const path = samplePolygon(polygon[0], perPolygonBudget).map(
              (point) => [Number(point.lng), Number(point.lat)],
            );
            if (path.length > 0) {
              overlays.push({
                path: {
                  coordinates: path,
                  strokeWidth: 2,
                  strokeColor: "#007bff",
                  strokeOpacity: 0.9,
                  fillColor: "#007bff",
                  fillOpacity: 0.2,
                },
              });
            }
          });
        } else if (radiusMiles) {
          // Add a marker overlay
          overlays.push({
            marker: {
              coordinates: numericCenter,
              size: "large",
              color: "#3FB1CE",
            },
          });

          // Convert radius from miles to kilometers and decrease by 25%
          const radiusKm = Number(radiusMiles) * 1.60934 * 0.72;
          // Create a circle as a polygon
          const circleCoords = createCirclePolygon(
            numericCenter[0],
            numericCenter[1],
            radiusKm,
          );

          // Calculate bounding box for the circle, expanded for context
          const circleBounds = expandBounds(
            calculateBounds(circleCoords),
            getContextFactor(radiusMiles),
          );

          // Adjust view to circle bounds with padding
          requestOptions.position = {
            bbox: circleBounds,
          };
          requestOptions.padding = String(padding);

          overlays.push({
            path: {
              coordinates: circleCoords,
              strokeWidth: 2,
              strokeColor: "#007bff",
              strokeOpacity: 0.9,
              fillColor: "#007bff",
              fillOpacity: 0.1,
            },
          });
        } else {
          // Default: only add a marker overlay
          overlays.push({
            marker: {
              coordinates: numericCenter,
              size: "small",
              color: "#3FB1CE",
            },
          });
        }

        requestOptions.overlays = overlays;

        const request = staticClient.getStaticImage(requestOptions);
        const imageUrl = request.url();

        setMapUrl(imageUrl);
      } catch (error) {
        console.error("Error generating static map:", error);
        // Fallback: generate a simple map with only a marker overlay

        let fallbackCoordinates = numericCenter;

        if (showPolygon && polygonCoordinates?.length > 0) {
          const firstPoint = get(polygonCoordinates, "0.0.0", null);
          if (firstPoint?.lng != null && firstPoint?.lat != null) {
            fallbackCoordinates = [Number(firstPoint.lng), Number(firstPoint.lat)];
          }
        }

        try {
          const fallbackOptions = {
            ownerId: styleOwner,
            styleId: styleId,
            width: reqWidth,
            height: reqHeight,
            position: {
              coordinates: fallbackCoordinates,
              zoom: zoom,
            },
            highRes: true,
            attribution: false,
            overlays: [
              {
                marker: {
                  coordinates: fallbackCoordinates,
                  size: "large",
                  color: "#3FB1CE",
                },
              },
            ],
          };

          const fallbackRequest = staticClient.getStaticImage(fallbackOptions);
          const fallbackUrl = fallbackRequest.url();
          setMapUrl(fallbackUrl);
        } catch (fallbackError) {
          console.error("Error generating fallback map:", fallbackError);
        }
      }
    };

    generateMap();
  }, [
    center,
    zoom,
    radiusMiles,
    width,
    height,
    showPolygon,
    polygonCoordinates,
    pendingPolygon,
    isVisible,
  ]);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: `100%`,
        height: `${height}px`,
        // Transparent, not a card-matching color: this div has square corners
        // while its img child and .property-img-wrap parent are both rounded
        // (and the parent doesn't clip), so an opaque fill paints visible
        // corner wedges. Transparent makes those wedges non-painting on any
        // surface color instead of matching just one.
        backgroundColor: "transparent",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {mapUrl && (
        <img
          src={mapUrl}
          alt="Static map"
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
    </div>
  );
};

export default memo(StaticMapboxComponent);
